/**
 * Return Request Module
 * Handles the full return request flow using a backend proxy
 * that connects to Shopify Admin API for creating real returns.
 *
 * SETUP: Deploy the Cloudflare Worker (return-proxy-worker.js)
 * and set RETURN_PROXY_URL below to your worker URL.
 */
const ReturnRequest = (() => {
  'use strict';

  /** Traducciones desde Liquid (#rr-i18n-json); si falta, se usan valores por defecto en español */
  let rrI18n = null;

  function loadRrI18n() {
    const el = document.getElementById('rr-i18n-json');
    if (!el) {
      rrI18n = null;
      return;
    }
    try {
      rrI18n = JSON.parse(el.textContent);
    } catch (e) {
      rrI18n = null;
    }
  }

  function S() {
    return (rrI18n && rrI18n.strings) || {};
  }

  function getReasonLabels() {
    if (rrI18n && rrI18n.reasonLabels) return rrI18n.reasonLabels;
    return REASON_LABELS_ES;
  }

  /**
   * ====================================================
   * CONFIGURATION — Set your proxy URL here
   * ====================================================
   * This URL points to your backend proxy (Cloudflare Worker, Vercel, etc.)
   * that forwards requests to the Shopify Admin API.
   *
   * Example: 'https://return-proxy.your-domain.workers.dev'
   */
  let PROXY_URL = '';

  let orderData = null;
  let currentStep = 0;
  let returnType = null; // 'desistimiento' | 'incidencia'

  /** packageNo incremental: 10000, 20000, 30000... */
  const PACKAGE_NO_BASE = 10000;
  let nextPackageNo = PACKAGE_NO_BASE;
  const BULTO_LIMITS_DEFAULT = { maxDimsCm: [100, 120, 220], maxWeightKg: 1000 };
  const BULTO_LIMITS_SEUR = { maxGirthCm: 300, maxWeightKg: 31 };

  function isSeurCarrier(carrier) {
    // Entrega en punto SEUR: siempre límites SEUR (independiente del carrier del pedido)
    if (collectionMethod === 'seur_point') return true;
    if (carrier == null || carrier === '') return false;
    return String(carrier).toUpperCase().includes('SEUR');
  }

  function getOrderDeliveryCarrier() {
    return orderData?.deliveryCarrier || '';
  }

  function seurGirthCm(length, width, height) {
    const dims = [Number(length), Number(width), Number(height)];
    if (dims.some((n) => !Number.isFinite(n) || n <= 0)) return NaN;
    dims.sort((a, b) => a - b);
    const [small, medium, large] = dims;
    return large + 2 * medium + 2 * small;
  }

  function dimensionsWithinDefaultMax(length, width, height) {
    const dims = [length, width, height].map(Number).sort((a, b) => a - b);
    const max = BULTO_LIMITS_DEFAULT.maxDimsCm;
    return dims[0] <= max[0] && dims[1] <= max[1] && dims[2] <= max[2];
  }

  function formatBultoTpl(str, vars) {
    let out = str || '';
    Object.keys(vars || {}).forEach((k) => {
      out = out.replace(new RegExp('\\{' + k + '\\}', 'g'), String(vars[k]));
    });
    return out;
  }

  function bultoLimitMessages() {
    const s = S();
    return {
      seurWeight: s.bultoSeurWeightMax || 'SEUR: peso máximo 31 kg por bulto',
      seurGirth: s.bultoSeurGirthMax || 'SEUR: envergadura máx. 3 m (mayor + 2×mediano + 2×pequeño)',
      defaultWeight: s.bultoDefaultWeightMax || 'Peso máximo 1000 kg por bulto',
      defaultDims: s.bultoDefaultDimsMax || 'Medidas máximas por bulto: 100×120×220 cm',
      limitsHintSeur: s.bultoLimitsHintSeur || 'Límites SEUR por bulto: envergadura máx. 3 m y peso máx. 31 kg.',
      limitsHintDefault: s.bultoLimitsHintDefault || 'Límites por bulto: máx. 100×120×220 cm y 1000 kg.',
      measureTip: s.bultoMeasureTip || 'Mide el paquete ya embalado, por el lado más largo de cada dimensión.',
      limitsTitleSeur: s.bultoLimitsTitleSeur || 'Límites de tu transportista (SEUR)',
      limitsTitleDefault: s.bultoLimitsTitleDefault || 'Límites por bulto',
      maxWeightLabel: s.bultoMaxWeightLabel || 'Peso máximo',
      maxGirthLabel: s.bultoMaxGirthLabel || 'Envergadura máxima',
      maxDimsLabel: s.bultoMaxDimsLabel || 'Medidas máximas (L × A × H)',
      girthFormula: s.bultoGirthFormula || 'Mayor + 2×Mediano + 2×Pequeño',
      weightShortError: s.bultoWeightShortError || 'Máximo {max} kg',
      girthShortError: s.bultoGirthShortError || 'La envergadura supera los 3 m',
      dimsShortError: s.bultoDimsShortError || 'Alguna medida supera el límite permitido',
      statusOk: s.bultoStatusOk || 'Dentro de límites',
      statusPending: s.bultoStatusPending || 'Completa las medidas',
      statusError: s.bultoStatusError || 'Fuera de límites',
      usedOfMax: s.bultoUsedOfMax || '{used} / {max}',
      labelLength: s.bultoLabelLength || 'Largo',
      labelWidth: s.bultoLabelWidth || 'Ancho',
      labelHeight: s.bultoLabelHeight || 'Alto',
      labelWeight: s.bultoLabelWeight || 'Peso'
    };
  }

  function bultoFormStrings() {
    const s = S();
    const msg = bultoLimitMessages();
    return {
      cardTitle: (n) => formatBultoTpl(s.bultoCardTitle || 'Bulto {n}', { n }),
      removeAria: s.bultoRemoveAria || 'Quitar bulto',
      fieldLength: s.bultoFieldLength || 'Longitud (cm)',
      fieldWidth: s.bultoFieldWidth || 'Anchura (cm)',
      fieldHeight: s.bultoFieldHeight || 'Altura (cm)',
      fieldWeight: s.bultoFieldWeight || 'Peso (kg)',
      inputTitle: s.bultoInputMeasureTitle || msg.measureTip,
      meterDimsLabel: s.bultoMeterDimsLabel || 'Medidas',
      meterGirthLabel: msg.maxGirthLabel,
      meterWeightLabel: msg.labelWeight
    };
  }

  function bultoMeterLevel(pct) {
    if (pct > 100) return 'error';
    if (pct >= 85) return 'warn';
    if (pct > 0) return 'ok';
    return 'idle';
  }

  function getBultoUsage(card) {
    const { length, width, height, weight } = getBultoMetrics(card);
    const complete = ![length, width, height, weight].some((n) => isNaN(n));
    const seur = isSeurCarrier(getOrderDeliveryCarrier());
    if (!complete) {
      return { complete: false, seur, valid: false, weight: NaN, girth: NaN, maxWeight: seur ? BULTO_LIMITS_SEUR.maxWeightKg : BULTO_LIMITS_DEFAULT.maxWeightKg, maxGirth: BULTO_LIMITS_SEUR.maxGirthCm, weightOk: false, girthOk: false, dimsOk: false };
    }
    if (seur) {
      const girth = seurGirthCm(length, width, height);
      const weightOk = weight <= BULTO_LIMITS_SEUR.maxWeightKg;
      const girthOk = girth <= BULTO_LIMITS_SEUR.maxGirthCm;
      return { complete: true, seur: true, valid: weightOk && girthOk, weight, girth, maxWeight: BULTO_LIMITS_SEUR.maxWeightKg, maxGirth: BULTO_LIMITS_SEUR.maxGirthCm, weightOk, girthOk, dimsOk: true, length, width, height };
    }
    const dimsOk = dimensionsWithinDefaultMax(length, width, height);
    const weightOk = weight <= BULTO_LIMITS_DEFAULT.maxWeightKg;
    return { complete: true, seur: false, valid: weightOk && dimsOk, weight, girth: 0, maxWeight: BULTO_LIMITS_DEFAULT.maxWeightKg, maxGirth: 0, weightOk, girthOk: true, dimsOk, length, width, height, maxDims: BULTO_LIMITS_DEFAULT.maxDimsCm };
  }

  function readBultoInputNumber(input) {
    if (!input) return NaN;
    const raw = String(input.value ?? '').trim();
    if (!raw) return NaN;
    const n = input.valueAsNumber;
    if (Number.isFinite(n) && n > 0) return n;
    const parsed = parseFloat(raw.replace(',', '.'), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : NaN;
  }

  function getBultoMetrics(card) {
    const read = (name) => {
      const input = $(`.rr-bulto-input[data-field="${name}"]`, card);
      return readBultoInputNumber(input);
    };
    return {
      length: read('length'),
      width: read('width'),
      height: read('height'),
      weight: read('weight')
    };
  }

  /** @returns {{ valid: boolean, fields: Record<string, string>, usage: object }} */
  function validateBultoCardLimits(card) {
    const msg = bultoLimitMessages();
    const fields = { length: '', width: '', height: '', weight: '' };
    const usage = getBultoUsage(card);
    if (!usage.complete) return { valid: false, fields, usage };
    if (!usage.weightOk) {
      fields.weight = formatBultoTpl(msg.weightShortError, { max: usage.maxWeight });
    }
    if (usage.seur && !usage.girthOk) {
      fields._girth = msg.girthShortError;
    } else if (!usage.seur && !usage.dimsOk) {
      fields._dims = msg.dimsShortError;
    }
    const valid = usage.valid;
    return { valid, fields, usage };
  }

  function packagingLimitsPanelEl() {
    return $('#rr-packaging-limits-panel') || $('#rr-packaging-limits-hint');
  }

  function renderPackagingLimitsPanel() {
    const el = packagingLimitsPanelEl();
    if (!el) return;
    const show = isCustomBultosPackagingVisible() && !$('#rr-packaging-original')?.checked;
    if (!show) {
      el.style.display = 'none';
      el.innerHTML = '';
      return;
    }
    const msg = bultoLimitMessages();
    const seur = isSeurCarrier(getOrderDeliveryCarrier());
    el.className = 'rr-packaging-limits-panel';
    el.setAttribute('role', 'region');
    if (seur) {
      el.innerHTML = `
        <div class="rr-limits-panel__head">
          <span class="rr-limits-panel__badge">SEUR</span>
          <div>
            <p class="rr-limits-panel__title">${escHtml(msg.limitsTitleSeur)}</p>
            <p class="rr-limits-panel__tip">${escHtml(msg.measureTip)}</p>
          </div>
        </div>
        <div class="rr-limits-panel__stats">
          <div class="rr-limits-stat">
            <span class="rr-limits-stat__icon" aria-hidden="true">⚖</span>
            <span class="rr-limits-stat__label">${escHtml(msg.maxWeightLabel)}</span>
            <strong class="rr-limits-stat__value">31 kg</strong>
          </div>
          <div class="rr-limits-stat">
            <span class="rr-limits-stat__icon" aria-hidden="true">📐</span>
            <span class="rr-limits-stat__label">${escHtml(msg.maxGirthLabel)}</span>
            <strong class="rr-limits-stat__value">3 m</strong>
            <span class="rr-limits-stat__sub">${escHtml(msg.girthFormula)}</span>
          </div>
        </div>`;
    } else {
      const d = BULTO_LIMITS_DEFAULT.maxDimsCm;
      el.innerHTML = `
        <div class="rr-limits-panel__head">
          <div>
            <p class="rr-limits-panel__title">${escHtml(msg.limitsTitleDefault)}</p>
            <p class="rr-limits-panel__tip">${escHtml(msg.measureTip)}</p>
          </div>
        </div>
        <div class="rr-limits-panel__stats">
          <div class="rr-limits-stat">
            <span class="rr-limits-stat__icon" aria-hidden="true">📦</span>
            <span class="rr-limits-stat__label">${escHtml(msg.maxDimsLabel)}</span>
            <strong class="rr-limits-stat__value">${d[0]}×${d[1]}×${d[2]} cm</strong>
          </div>
          <div class="rr-limits-stat">
            <span class="rr-limits-stat__icon" aria-hidden="true">⚖</span>
            <span class="rr-limits-stat__label">${escHtml(msg.maxWeightLabel)}</span>
            <strong class="rr-limits-stat__value">${BULTO_LIMITS_DEFAULT.maxWeightKg} kg</strong>
          </div>
        </div>`;
    }
    el.style.display = 'block';
  }

  function updatePackagingLimitsHint() {
    renderPackagingLimitsPanel();
  }

  function ensureBultoCardChrome(card) {
    if (!card || card.dataset.chromeReady === '1') return;
    const ui = bultoFormStrings();
    const header = $('.rr-bulto-card__header', card);
    if (header && !$('.rr-bulto-card__status', card)) {
      const status = document.createElement('span');
      status.className = 'rr-bulto-card__status';
      status.setAttribute('data-bulto-status', '');
      header.insertBefore(status, $('.rr-bulto-remove', card));
    }
    if (!$('[data-bulto-meters]', card)) {
      const meters = document.createElement('div');
      meters.className = 'rr-bulto-meters';
      meters.setAttribute('data-bulto-meters', '');
      meters.innerHTML = `
        <div class="rr-bulto-meter" data-meter="weight">
          <div class="rr-bulto-meter__top"><span data-meter-label>${escHtml(ui.meterWeightLabel)}</span><span data-meter-value>—</span></div>
          <div class="rr-bulto-meter__track"><span class="rr-bulto-meter__fill" data-meter-fill></span></div>
        </div>
        <div class="rr-bulto-meter rr-bulto-meter--girth" data-meter="girth">
          <div class="rr-bulto-meter__top"><span data-meter-label>${escHtml(ui.meterGirthLabel)}</span><span data-meter-value>—</span></div>
          <div class="rr-bulto-meter__track"><span class="rr-bulto-meter__fill" data-meter-fill></span></div>
          <p class="rr-bulto-meter__formula" data-girth-formula></p>
        </div>
        <div class="rr-bulto-meter rr-bulto-meter--dims" data-meter="dims" hidden>
          <div class="rr-bulto-meter__top"><span data-meter-label>${escHtml(ui.meterDimsLabel)}</span><span data-meter-value>—</span></div>
          <div class="rr-bulto-meter__track"><span class="rr-bulto-meter__fill" data-meter-fill></span></div>
        </div>`;
      const grid = $('.rr-bulto-card__grid', card);
      if (grid) card.insertBefore(meters, grid);
    }
    if (!$('[data-dims-alert]', card)) {
      const alert = document.createElement('p');
      alert.className = 'rr-bulto-dims-alert';
      alert.setAttribute('data-dims-alert', '');
      alert.hidden = true;
      const grid = $('.rr-bulto-card__grid', card);
      if (grid) card.insertBefore(alert, grid.nextSibling);
    }
    card.dataset.chromeReady = '1';
  }

  function setBultoMeter(card, name, used, max, label) {
    const meter = $(`[data-meter="${name}"]`, card);
    if (!meter) return;
    const pct = max > 0 && Number.isFinite(used) ? Math.min(150, (used / max) * 100) : 0;
    const level = bultoMeterLevel(pct);
    meter.className = 'rr-bulto-meter rr-bulto-meter--' + name + (level !== 'idle' ? ' rr-bulto-meter--' + level : '');
    const fill = $('[data-meter-fill]', meter);
    const valEl = $('[data-meter-value]', meter);
    const labelEl = $('[data-meter-label]', meter);
    if (labelEl && label) labelEl.textContent = label;
    if (fill) fill.style.width = (Number.isFinite(used) ? Math.min(100, pct) : 0) + '%';
    if (valEl) {
      const msg = bultoLimitMessages();
      valEl.textContent = Number.isFinite(used)
        ? formatBultoTpl(msg.usedOfMax, { used: name === 'girth' ? (used / 100).toFixed(2) + ' m' : Math.round(used * 10) / 10 + (name === 'weight' ? ' kg' : ' cm'), max: name === 'girth' ? '3 m' : max + (name === 'weight' ? ' kg' : ' cm') })
        : '—';
    }
  }

  function updateBultoCardVisuals(card) {
    ensureBultoCardChrome(card);
    const msg = bultoLimitMessages();
    const usage = getBultoUsage(card);
    const limits = validateBultoCardLimits(card);
    const statusEl = $('[data-bulto-status]', card);
    const dimsAlert = $('[data-dims-alert]', card);
    const girthMeter = $('[data-meter="girth"]', card);
    const dimsMeter = $('[data-meter="dims"]', card);

    if (girthMeter) girthMeter.hidden = !usage.seur;
    if (dimsMeter) dimsMeter.hidden = usage.seur;

    if (usage.seur) {
      setBultoMeter(card, 'weight', usage.weight, usage.maxWeight, msg.labelWeight);
      setBultoMeter(card, 'girth', usage.girth, usage.maxGirth, msg.maxGirthLabel);
      const formulaEl = $('[data-girth-formula]', card);
      if (formulaEl) formulaEl.textContent = msg.girthFormula;
    } else if (usage.complete) {
      const d = usage.maxDims || BULTO_LIMITS_DEFAULT.maxDimsCm;
      const sorted = [usage.length, usage.width, usage.height].sort((a, b) => a - b);
      const worst = Math.max(sorted[0] / d[0], sorted[1] / d[1], sorted[2] / d[2]) * 100;
      setBultoMeter(card, 'dims', worst, 100, msg.maxDimsLabel);
      setBultoMeter(card, 'weight', usage.weight, usage.maxWeight, msg.labelWeight);
    } else {
      setBultoMeter(card, 'weight', NaN, usage.maxWeight, msg.labelWeight);
      if (usage.seur) setBultoMeter(card, 'girth', NaN, usage.maxGirth, msg.maxGirthLabel);
    }

    if (dimsAlert) {
      const alertText = limits.fields._girth || limits.fields._dims || '';
      dimsAlert.textContent = alertText;
      dimsAlert.hidden = !alertText;
    }

    if (statusEl) {
      let status = 'pending';
      let text = msg.statusPending;
      if (usage.complete) {
        status = limits.valid ? 'ok' : 'error';
        text = limits.valid ? msg.statusOk : msg.statusError;
      }
      statusEl.className = 'rr-bulto-card__status rr-bulto-card__status--' + status;
      statusEl.textContent = text;
    }

    card.classList.toggle('rr-bulto-card--valid', !!limits.valid);
    card.classList.toggle('rr-bulto-card--invalid', usage.complete && !limits.valid);

    $$('.rr-bulto-field--dim', card).forEach((el) => el.classList.remove('rr-bulto-field--warn'));
    if (limits.fields._girth || limits.fields._dims) {
      $$('.rr-bulto-field--dim', card).forEach((el) => el.classList.add('rr-bulto-field--warn'));
    }
  }

  let collectionMethod = null; // 'pickup' | 'self' | 'seur_point'
  let collectionAddressType = 'same'; // 'same' = usar dirección de envío; 'new' = rellenar formulario
  let collectionAddress = null; // { name, address1, address2, city, zip, phone, province, countryCode }
  let selectedSeurPickup = null; // { code, name, address, postalCode, city, schedule, ... }

  /**
   * Feature SEUR punto: .es / .pt + ng-vat-dev (pruebas).
   * NL (newgardenshopping.nl) excluido: SEUR sin soporte API internacional.
   */
  /** Ajustes de devoluciones de la tienda (bloque «config» del JSON de textos). */
  function rrConfig() {
    return (rrI18n && rrI18n.config) || {};
  }

  function isSeurPointHost() {
    try {
      if (rrConfig().seurPoint === false) return false;
      const host = String(window.location.hostname || '').toLowerCase();
      return host === 'newgardenshop.es'
        || host === 'www.newgardenshop.es'
        || host === 'newgardenshop.pt'
        || host === 'www.newgardenshop.pt'
        || host === 'ng-vat-dev.myshopify.com'
        || host === 'www.ng-vat-dev.myshopify.com';
    } catch (e) {
      return false;
    }
  }

  /**
   * Aptitud punto SEUR:
   * - NL (newgardenshopping.nl): newgarden.disponible_pickup_nl
   * - resto: newgarden.disponible_pickup
   * Falso/vacío = no apto.
   */
  function isDisponiblePickupValue(v) {
    if (v === true || v === 1) return true;
    if (v == null || v === '') return false;
    const s = String(v).trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes' || s === 'si' || s === 'sí';
  }

  function isNlPickupHost() {
    try {
      const host = String(window.location.hostname || '').toLowerCase();
      return host === 'newgardenshopping.nl' || host === 'www.newgardenshopping.nl';
    } catch (e) {
      return false;
    }
  }

  function lineItemAllowsSeurPickup(item) {
    if (!item) return false;
    if (isNlPickupHost()) {
      return isDisponiblePickupValue(item.disponiblePickupNl != null ? item.disponiblePickupNl : item.disponiblePickup);
    }
    return isDisponiblePickupValue(item.disponiblePickup);
  }

  function findOrderLineItem(lineItemId) {
    const id = String(lineItemId || '');
    const items = orderData?.lineItems || [];
    return items.find((i) => {
      const iid = String(i.id || '');
      return iid === id || iid.endsWith('/' + id) || id.endsWith('/' + iid);
    }) || null;
  }

  /**
   * Todos los productos seleccionados deben permitir pickup
   * (disponible_pickup o disponible_pickup_nl en NL).
   */
  function selectedProductsAllowSeurPickup() {
    const cards = $$('.rr-product-card').filter((card) => {
      if (card.dataset.noReturn === 'true') return false;
      const cb = $('.rr-checkbox', card);
      return !!(cb && cb.checked);
    });
    if (!cards.length) return false;
    return cards.every((card) => {
      const rawAttr = card.getAttribute('data-disponible-pickup');
      if (rawAttr !== null && rawAttr !== '') {
        return isDisponiblePickupValue(rawAttr);
      }
      const item = findOrderLineItem(card.dataset.lineItemId);
      return lineItemAllowsSeurPickup(item);
    });
  }

  function isSeurPointFeatureEnabled() {
    return isSeurPointHost()
      && !!$('#rr-seur-point-section')
      && selectedProductsAllowSeurPickup();
  }

  /** Muestra/oculta tarjeta SEUR según host + metafield de productos seleccionados. */
  function updateSeurPointOptionVisibility() {
    const seurCard = $('.rr-collection-card[data-method="seur_point"]');
    const seurSection = $('#rr-seur-point-section');
    if (!seurCard && !seurSection) return;

    const hostOk = isSeurPointHost();
    const productsOk = selectedProductsAllowSeurPickup();
    const show = hostOk && productsOk && !!seurSection;

    if (seurCard) {
      seurCard.style.display = show ? '' : 'none';
      if (!show) {
        const radio = $('.rr-collection-radio', seurCard);
        if (radio) radio.checked = false;
        seurCard.classList.remove('rr-collection-card--selected');
      }
    }

    if (!show) {
      if (seurSection) seurSection.style.display = 'none';
      if (collectionMethod === 'seur_point') {
        selectedSeurPickup = null;
        const pickup = $('#rr-collection-pickup');
        const pickupCard = $('.rr-collection-card[data-method="pickup"]');
        if (pickup) pickup.checked = true;
        collectionMethod = 'pickup';
        $$('.rr-collection-card').forEach((c) => c.classList.remove('rr-collection-card--selected'));
        if (pickupCard) pickupCard.classList.add('rr-collection-card--selected');
        updateStep3UI();
      }
    }
  }

  /** Shop para proxy: preferir myshopify; mapear custom domain ES/PT/NL / sandbox. */
  function getShopDomain() {
    const fromShopify = String(window.Shopify?.shop || '').trim().toLowerCase();
    if (fromShopify) return fromShopify;
    const host = String(window.location.hostname || '').toLowerCase();
    if (host === 'newgardenshop.es' || host === 'www.newgardenshop.es'
      || host === 'newgardenshop.pt' || host === 'www.newgardenshop.pt'
      || host === 'newgardenshopping.nl' || host === 'www.newgardenshopping.nl') {
      return 'new-garden-shop.myshopify.com';
    }
    if (host === 'ng-vat-dev.myshopify.com' || host === 'www.ng-vat-dev.myshopify.com') {
      return 'ng-vat-dev.myshopify.com';
    }
    return host;
  }

  /** Motivos (ES por defecto si no hay #rr-i18n-json). BC: returnReasonDescription = texto mostrado */
  const REASON_LABELS_ES = {
    PRODUCT_TOO_BIG: 'El producto es más grande de lo que pensaba.',
    PRODUCT_TOO_SMALL: 'El producto es más pequeño de lo que pensaba.',
    COLOR_DIFFERENT: 'El color es diferente al que esperaba.',
    FOUND_CHEAPER: 'He encontrado el producto más barato en otro lugar.',
    NO_LONGER_NEED: 'Ya no lo necesito.',
    BOUGHT_ELSEWHERE: 'Lo he comprado en otra tienda.',
    DOES_NOT_MEET_EXPECTATIONS: 'El producto no cumple con mis expectativas.',
    NOT_AS_DESCRIBED: 'El producto no es lo que esperaba según la descripción.',
    QUALITY_PROBLEMS: 'Problemas con la calidad del producto.',
    DONT_LIKE: 'No me gusta el producto.',
    CHANGE_OF_MIND: 'Cambio de opinión.',
    NOT_INTERESTED: 'No me interesa el producto.',
    NO_SE_COMO_FUNCIONA: 'No sé cómo funciona.',
    OTHER: 'Otro motivo.'
  };
  const REASON_NO_SE_COMO_FUNCIONA = 'NO_SE_COMO_FUNCIONA';

  /** Tabla de gastos de recogida (importes en €). Orden: primer tramo que cumpla total ≤ max. */
  const RETURN_COST_TABLE_DEFAULT = [
    { max: 50, cost: 15 },
    { max: 100, cost: 25 },
    { max: 150, cost: 30 },
    { max: 300, cost: 45 },
    { max: 500, cost: 75 },
    { max: 700, cost: 105 },
    { max: 1500, cost: 180 },
    { max: 2000, cost: 200 }
  ];

  /** newgardenshopping.nl: primer tramo hasta 50 € = 20 € (resto igual que default). */
  const RETURN_COST_TABLE_NL = [
    { max: 50, cost: 20 },
    { max: 100, cost: 25 },
    { max: 150, cost: 30 },
    { max: 300, cost: 45 },
    { max: 500, cost: 75 },
    { max: 700, cost: 105 },
    { max: 1500, cost: 180 },
    { max: 2000, cost: 200 }
  ];

  function isNlReturnCostHost() {
    try {
      if (rrConfig().costTable === 'alt') return true;
      return typeof window !== 'undefined' && window.location.hostname.indexOf('newgardenshopping.nl') !== -1;
    } catch (e) {
      return false;
    }
  }

  function getReturnCostTable() {
    return isNlReturnCostHost() ? RETURN_COST_TABLE_NL : RETURN_COST_TABLE_DEFAULT;
  }

  function getReturnCost(totalEuros) {
    if (collectionMethod === 'self') return 0; // envío por cuenta del cliente → sin coste
    const table = getReturnCostTable();
    for (let i = 0; i < table.length; i++) {
      if (totalEuros <= table[i].max) return table[i].cost;
    }
    return null; // > 2.000 € → consultar gastos por código postal
  }

  function getMoneyLocale() {
    let locale = 'es-ES';
    if (rrI18n && rrI18n.moneyLocale) locale = rrI18n.moneyLocale;
    else if (typeof window !== 'undefined') {
      const h = window.location.hostname;
      if (h.indexOf('newgardenshop.pt') !== -1) locale = 'pt-PT';
      else if (h.indexOf('newgardenshopping.nl') !== -1) locale = 'nl-NL';
    }
    return locale;
  }

  function formatMoney(euros) {
    return euros.toLocaleString(getMoneyLocale(), { style: 'currency', currency: 'EUR' });
  }

  function formatDeliveryDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toLocaleDateString(getMoneyLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
  }

  function withdrawalDetailRow(label, value, muted) {
    const valueClass = muted ? 'rr-withdrawal-detail-value is-muted' : 'rr-withdrawal-detail-value';
    const cleanLabel = String(label || '').replace(/:+\s*$/, '');
    return `<div class="rr-withdrawal-detail-row">
      <span class="rr-withdrawal-detail-label">${escHtml(cleanLabel)}</span>
      <span class="${valueClass}">${escHtml(value)}</span>
    </div>`;
  }

  function populateWithdrawalBlockedDetails() {
    const detailsEl = $('#rr-withdrawal-blocked-details');
    if (!detailsEl) return;
    if (orderData?.withdrawalBlockReason === 'not_delivered') {
      detailsEl.innerHTML = '';
      detailsEl.style.display = 'none';
      return;
    }
    const strings = S();
    const rows = [];
    const dateStr = formatDeliveryDate(orderData?.lastDeliveredAt);
    if (dateStr && strings.withdrawalDeliveryLabel) {
      rows.push(withdrawalDetailRow(strings.withdrawalDeliveryLabel, dateStr, false));
    }
    if (!rows.length) {
      detailsEl.innerHTML = '';
      detailsEl.style.display = 'none';
      return;
    }
    detailsEl.innerHTML = rows.join('');
    detailsEl.style.display = '';
  }

  function escHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isProductCardNoReturn(card) {
    return card && (card.classList.contains('rr-product-card--no-return') || card.dataset.noReturn === 'true');
  }

  function getLineUnitEuros(item) {
    if (!item) return 0;
    if (item.priceCents != null && item.priceCents !== '') {
      return (Number(item.priceCents) || 0) / 100;
    }
    const pricing = orderData?.pricing;
    const ratio = pricing && Number(pricing.subtotalCents) > 0
      ? (Number(pricing.goodsPaidCents) || Number(pricing.subtotalCents)) / Number(pricing.subtotalCents)
      : 1;
    return (Number(item.price) || 0) * ratio;
  }

  function getLineGrossUnitEuros(item) {
    if (!item) return 0;
    if (item.grossPriceCents != null && item.grossPriceCents !== '') {
      return (Number(item.grossPriceCents) || 0) / 100;
    }
    return getLineUnitEuros(item);
  }

  /** Importe bruto de la selección (céntimos enteros, sin redondeo por unidad). */
  function getLineGrossEuros(item, selectedQty) {
    if (!item) return 0;
    const lineQty = Math.max(1, parseInt(item.quantity, 10) || 1);
    const qty = Math.max(1, Math.min(selectedQty, lineQty));
    if (item.grossLineTotalCents != null && item.grossLineTotalCents !== '') {
      return Math.round(Number(item.grossLineTotalCents) * qty / lineQty) / 100;
    }
    return getLineGrossUnitEuros(item) * qty;
  }

  function getRefundBreakdown() {
    let grossCents = 0;
    $$('.rr-product-card').forEach(card => {
      if (isProductCardNoReturn(card)) return;
      const cb = $('.rr-checkbox', card);
      if (!cb || !cb.checked) return;
      const qty = Math.max(1, parseInt($('.rr-qty-input', card)?.value, 10) || 1);
      const lineItemId = card.dataset.lineItemId;
      const item = orderData?.lineItems?.find(i => String(i.id) === String(lineItemId));
      if (!item) return;
      grossCents += Math.round(getLineGrossEuros(item, qty) * 100);
    });

    const pricing = orderData?.pricing;
    let paidCents = grossCents;
    if (pricing && Number(pricing.subtotalCents) > 0 && grossCents > 0) {
      paidCents = Math.round(Number(pricing.goodsPaidCents) * grossCents / Number(pricing.subtotalCents));
    } else {
      let paidFallback = 0;
      $$('.rr-product-card').forEach(card => {
        if (isProductCardNoReturn(card)) return;
        const cb = $('.rr-checkbox', card);
        if (!cb || !cb.checked) return;
        const qty = Math.max(1, parseInt($('.rr-qty-input', card)?.value, 10) || 1);
        const lineItemId = card.dataset.lineItemId;
        const item = orderData?.lineItems?.find(i => String(i.id) === String(lineItemId));
        if (!item) return;
        if (item.paidLineTotalCents != null && item.paidLineTotalCents !== '') {
          const lineQty = Math.max(1, parseInt(item.quantity, 10) || 1);
          paidFallback += Math.round(Number(item.paidLineTotalCents) * qty / lineQty);
        } else {
          paidFallback += Math.round(getLineUnitEuros(item) * qty * 100);
        }
      });
      paidCents = paidFallback;
    }

    const discountCents = Math.max(0, grossCents - paidCents);
    return {
      grossSubtotal: grossCents / 100,
      discount: discountCents / 100,
      paidSubtotal: paidCents / 100
    };
  }

  function getSelectedTotalEuros() {
    return getRefundBreakdown().paidSubtotal;
  }

  /* ---------- DOM Helpers ---------- */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  function overlay() { return $('#ReturnRequestOverlay'); }

  /** Muestra u oculta el bloque informativo cuando se selecciona "No sé cómo funciona" */
  function toggleNoSeComoFuncionaBlock(show) {
    const block = $('#rr-info-block-no-se-como-funciona');
    if (!block) return;
    block.classList.toggle('rr-info-block--hidden', !show);
  }

  /* ---------- Init ---------- */
  function init() {
    loadRrI18n();
    // Read order data
    const dataEl = $('#rr-order-data');
    if (dataEl) {
      try {
        orderData = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[ReturnRequest] Error parsing order data:', e);
      }
    }

    // Read proxy URL from config element (set in Liquid)
    const configEl = $('#rr-config');
    if (configEl) {
      try {
        const config = JSON.parse(configEl.textContent);
        if (config.proxyUrl) PROXY_URL = config.proxyUrl;
      } catch (e) { /* ignore */ }
    }

    bindEvents();
    bindTypeStepEvents();
    bindPackagingEvents();
    bindCollectionMethodEvents();
    ensureQtyInputsVisible();
    bindReturnDrawer();
    checkReturnStatus();
  }

  /** Re-read #rr-order-data y #rr-config (para flujo invitado que inyecta datos tras lookup). */
  function reloadOrderData() {
    loadRrI18n();
    const dataEl = $('#rr-order-data');
    if (dataEl) {
      try {
        orderData = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[ReturnRequest] Error parsing order data:', e);
      }
    }
    const configEl = $('#rr-config');
    if (configEl) {
      try {
        const config = JSON.parse(configEl.textContent);
        if (config.proxyUrl) PROXY_URL = config.proxyUrl;
      } catch (e) { /* ignore */ }
    }
    collectionAddressType = 'same';
    collectionAddress = null; // Reset so it picks up new order's address
    ensureQtyInputsVisible();
    bindProductCardEvents();
    initCollectionAddress();
    updateSeurPointOptionVisibility();
  }

  /** Enlaza eventos de las tarjetas de producto (checkbox + validación paso 1). Necesario cuando se inyectan productos por JS (guest). */
  function bindProductCardEvents() {
    $$('.rr-product-card').forEach(card => {
      const cb = $('.rr-checkbox', card);
      if (!cb) return;
      card.classList.toggle('rr-selected', cb.checked);

      // Remove old card click handler — the <label> natively toggles the checkbox;
      // a manual toggle on top causes a double-toggle (no net change).
      if (card._rrCardClick) {
        card.removeEventListener('click', card._rrCardClick);
        card._rrCardClick = null;
      }

      // Stepper buttons: stop click from bubbling to label (would toggle checkbox)
      $$('.rr-stepper__btn', card).forEach(btn => {
        if (btn._rrStepperGuard) btn.removeEventListener('click', btn._rrStepperGuard);
        btn._rrStepperGuard = (e) => e.stopPropagation();
        btn.addEventListener('click', btn._rrStepperGuard);
      });

      // Checkbox: stop click from bubbling to label (would cause double toggle)
      if (cb._rrCbClick) cb.removeEventListener('click', cb._rrCbClick);
      cb._rrCbClick = (e) => e.stopPropagation();
      cb.addEventListener('click', cb._rrCbClick);

      // Sync UI on every checkbox change (fired by native label activation or direct click)
      if (cb._rrCbChange) cb.removeEventListener('change', cb._rrCbChange);
      cb._rrCbChange = function () {
        card.classList.toggle('rr-selected', cb.checked);
        validateStep1();
        updateSelectAllButton();
        updateSeurPointOptionVisibility();
      };
      cb.addEventListener('change', cb._rrCbChange);
    });
  }

  function bindReturnDrawer() {
    const trigger = $('#rr-return-drawer-trigger');
    const body = $('#rr-return-drawer-body');
    if (!trigger || !body) return;
    trigger.addEventListener('click', function () {
      const wasHidden = body.hidden;
      body.hidden = !wasHidden;
      trigger.setAttribute('aria-expanded', wasHidden ? 'true' : 'false');
      trigger.classList.toggle('rr-return-drawer__trigger--open', wasHidden);
      if (wasHidden) fetchAndApplyReturnStatus();
    });
  }

  function bindTypeStepEvents() {
    $$('.rr-type-card').forEach(card => {
      card.addEventListener('click', function () {
        const radio = $('.rr-type-radio', this);
        if (radio) radio.checked = true;
        $$('.rr-type-card').forEach(c => c.classList.remove('rr-type-card--selected'));
        this.classList.add('rr-type-card--selected');
        validateTypeStep();
      });
    });
    $$('.rr-type-radio').forEach(radio => {
      radio.addEventListener('change', function () {
        const card = this.closest('.rr-type-card');
        if (card) {
          $$('.rr-type-card').forEach(c => c.classList.remove('rr-type-card--selected'));
          card.classList.add('rr-type-card--selected');
        }
        validateTypeStep();
      });
    });
  }

  function validateTypeStep() {
    const btn = $('#rr-next-step0');
    const selected = $('.rr-type-radio:checked');
    if (btn) btn.disabled = !selected;
  }

  /** Vuelve al paso 0 desde la pantalla de incidencia */
  function goBackToTypeStep() {
    returnType = null;
    $$('.rr-type-radio').forEach(r => { r.checked = false; });
    $$('.rr-type-card').forEach(c => c.classList.remove('rr-type-card--selected'));
    validateTypeStep();
    showStep(0);
    currentStep = 0;
  }

  /** Público: llamado desde onclick cuando el usuario hace clic en Continuar en el paso 0 */
  function onTypeSelected() {
    const selected = $('.rr-type-radio:checked');
    if (!selected) return;
    returnType = selected.value;
    if (returnType === 'incidencia') {
      showStep('incidencia');
      currentStep = 'incidencia';
    } else if (returnType === 'desistimiento' && orderData?.withdrawalEligible === false) {
      // Flujo invitado: comprobación de plazo de desistimiento (15 días desde entrega a domicilio).
      showStep('withdrawal-blocked');
      currentStep = 'withdrawal-blocked';
      const notDelivered = orderData?.withdrawalBlockReason === 'not_delivered';
      const deadlineEl = $('#rr-withdrawal-blocked-message-deadline');
      const notDeliveredEl = $('#rr-withdrawal-blocked-message-not-delivered');
      if (deadlineEl && notDeliveredEl) {
        deadlineEl.style.display = notDelivered ? 'none' : '';
        notDeliveredEl.style.display = notDelivered ? '' : 'none';
      }
      populateWithdrawalBlockedDetails();
    } else {
      showStep(1);
      currentStep = 1;
    }
  }

  function bindEvents() {
    bindProductCardEvents();
    // Evitar que el click del checkbox propague y dispare el del card (doble toggle)
    $$('.rr-product-card .rr-checkbox').forEach(cb => {
      cb.addEventListener('click', (e) => e.stopPropagation());
    });

    // Reason radio buttons: validación + mostrar/ocultar bloque informativo "No sé cómo funciona"
    $$('.rr-reason-radio').forEach(radio => {
      radio.addEventListener('change', function () {
        validateStep2();
        toggleNoSeComoFuncionaBlock(radio.value === REASON_NO_SE_COMO_FUNCIONA);
      });
    });

    // Observations character count
    const textarea = $('#rr-observations');
    if (textarea) {
      textarea.addEventListener('input', () => {
        const counter = $('#rr-char-current');
        if (counter) counter.textContent = textarea.value.length;
      });
    }

    // Terms checkbox
    const terms = $('#rr-terms-checkbox');
    if (terms) {
      terms.addEventListener('change', validateStep3);
    }

    const requiresPickup = $('#rr-requires-pickup');
    if (requiresPickup) {
      requiresPickup.addEventListener('change', function () { /* no validation needed */ });
    }

    // Close on overlay click
    const ol = overlay();
    if (ol) {
      ol.addEventListener('click', (e) => {
        if (e.target === ol) close();
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && ol && ol.classList.contains('rr-active')) {
        close();
      }
    });
  }

  function bindPackagingEvents() {
    const originalMsg = $('#rr-packaging-original-message');
    const bultosSection = $('#rr-packaging-bultos-section');
    const originalRadio = $('#rr-packaging-original');

    function togglePackagingUI() {
      const isOriginal = !!originalRadio?.checked;
      if (originalMsg) originalMsg.style.display = isOriginal ? 'flex' : 'none';
      if (bultosSection) {
        bultosSection.classList.toggle('rr-packaging-bultos--hidden', isOriginal);
        if (!isOriginal && $('#rr-packaging-bultos-list')?.children.length === 0) {
          addBulto();
        }
      }
      updatePackagingLimitsHint();
      validatePackagingStep();
    }

    $$('.rr-packaging-radio').forEach(radio => {
      radio.addEventListener('change', togglePackagingUI);
    });
    $$('.rr-packaging-card').forEach(card => {
      card.addEventListener('click', () => setTimeout(togglePackagingUI, 0));
    });
    togglePackagingUI();

    const addBtn = $('#rr-add-bulto');
    if (addBtn) addBtn.addEventListener('click', () => { addBulto(); validatePackagingStep(); });

    document.addEventListener('input', function (e) {
      if (e.target?.classList?.contains('rr-bulto-input')) validatePackagingStep();
    });
    document.addEventListener('change', function (e) {
      if (e.target?.classList?.contains('rr-bulto-input')) validatePackagingStep();
    });
  }

  /* ---------- Collection Method (Step 3) ---------- */
  function bindCollectionMethodEvents() {
    updateSeurPointOptionVisibility();

    // Defensa: si el DOM tiene SEUR pero no aplica (host/productos), ocultar
    if (!isSeurPointHost() || !$('#rr-seur-point-section')) {
      $$('[data-seur-point-only="true"]').forEach((el) => { el.style.display = 'none'; });
      const seurSection = $('#rr-seur-point-section');
      if (seurSection) seurSection.style.display = 'none';
    }

    $$('.rr-collection-card').forEach(card => {
      card.addEventListener('click', function () {
        const radio = $('.rr-collection-radio', this);
        if (!radio) return;
        // Nunca activar self desde UI (opcion no ofrecida)
        if (radio.value === 'self') return;
        if (radio.value === 'seur_point' && !isSeurPointFeatureEnabled()) return;
        radio.checked = true;
        $$('.rr-collection-card').forEach(c => c.classList.remove('rr-collection-card--selected'));
        this.classList.add('rr-collection-card--selected');
        const prev = collectionMethod;
        collectionMethod = radio.value;
        if (prev === 'seur_point' && collectionMethod !== 'seur_point') {
          selectedSeurPickup = null;
          renderSelectedSeurPickup();
        }
        updateStep3UI();
        validatePackagingStep();
      });
    });
    $$('.rr-collection-radio').forEach(radio => {
      radio.addEventListener('change', function () {
        if (this.value === 'self') {
          this.checked = false;
          return;
        }
        if (this.value === 'seur_point' && !isSeurPointFeatureEnabled()) {
          this.checked = false;
          return;
        }
        const card = this.closest('.rr-collection-card');
        if (card) {
          $$('.rr-collection-card').forEach(c => c.classList.remove('rr-collection-card--selected'));
          card.classList.add('rr-collection-card--selected');
        }
        const prev = collectionMethod;
        collectionMethod = this.value;
        if (prev === 'seur_point' && collectionMethod !== 'seur_point') {
          selectedSeurPickup = null;
          renderSelectedSeurPickup();
        }
        updateStep3UI();
        validatePackagingStep();
      });
    });

    const copyBtn = $('#rr-copy-newgarden-address');
    if (copyBtn) copyBtn.addEventListener('click', copyNewgardenAddress);

    // Opción dirección: misma vs nueva
    $$('.rr-address-type-card').forEach(card => {
      card.addEventListener('click', function () {
        const radio = $('.rr-address-type-radio', this);
        if (!radio) return;
        radio.checked = true;
        $$('.rr-address-type-card').forEach(c => c.classList.remove('rr-address-type-card--selected'));
        this.classList.add('rr-address-type-card--selected');
        collectionAddressType = radio.value;
        updateAddressTypeUI();
        validatePackagingStep();
      });
    });
    $$('.rr-address-type-radio').forEach(radio => {
      radio.addEventListener('change', function () {
        const card = this.closest('.rr-address-type-card');
        if (card) {
          $$('.rr-address-type-card').forEach(c => c.classList.remove('rr-address-type-card--selected'));
          card.classList.add('rr-address-type-card--selected');
        }
        collectionAddressType = this.value;
        updateAddressTypeUI();
        validatePackagingStep();
      });
    });
    // Validar paso 3 cuando cambian los campos de nueva dirección
    ['#rr-addr-address1', '#rr-addr-zip', '#rr-addr-city', '#rr-addr-province', '#rr-addr-country'].forEach(sel => {
      const el = $(sel);
      if (!el) return;
      el.addEventListener('input', () => validatePackagingStep());
      el.addEventListener('change', () => validatePackagingStep());
    });

    const seurSearchBtn = $('#rr-seur-search-btn');
    if (seurSearchBtn) seurSearchBtn.addEventListener('click', searchSeurPickups);
    const seurChangeBtn = $('#rr-seur-change-btn');
    if (seurChangeBtn) seurChangeBtn.addEventListener('click', () => {
      selectedSeurPickup = null;
      renderSelectedSeurPickup();
      const wrap = $('#rr-seur-results-wrap');
      const results = $('#rr-seur-results');
      if (wrap && results && results.children.length) {
        wrap.hidden = false;
      }
      validatePackagingStep();
    });
    ['#rr-seur-zip', '#rr-seur-city'].forEach((sel) => {
      const el = $(sel);
      if (el) el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          searchSeurPickups();
        }
      });
    });
  }

  /** Solo recogida en domicilio: oculto el bloque de elección en Liquid; fuerza pickup en estado. */
  function applyPickupOnlyDefault() {
    const pickup = $('#rr-collection-pickup');
    const pickupCard = $('.rr-collection-card[data-method="pickup"]');
    if (!pickup) return;
    pickup.checked = true;
    const selfRadio = $('#rr-collection-self');
    if (selfRadio) selfRadio.checked = false;
    const seurRadio = $('#rr-collection-seur-point');
    if (seurRadio) seurRadio.checked = false;
    selectedSeurPickup = null;
    resetSeurPointUI();
    collectionMethod = 'pickup';
    $$('.rr-collection-card').forEach(c => c.classList.remove('rr-collection-card--selected'));
    if (pickupCard) pickupCard.classList.add('rr-collection-card--selected');
    updateStep3UI();
    validatePackagingStep();
  }

  /**
   * En hosts con SEUR (.es / ng-vat-dev): no pisar la elección al re-entrar al paso 3.
   * En otros hosts: sigue forzando solo pickup.
   */
  function syncStep3CollectionMethod() {
    updateSeurPointOptionVisibility();
    if (!isSeurPointFeatureEnabled()) {
      applyPickupOnlyDefault();
      return;
    }
    if (collectionMethod === 'seur_point' || collectionMethod === 'pickup') {
      updateStep3UI();
      validatePackagingStep();
      return;
    }
    applyPickupOnlyDefault();
  }

  function getDefaultPickupCountryCode() {
    const host = (window.location.hostname || '').toLowerCase();
    if (host.includes('newgardenshop.pt') || host.includes('newgarden.pt')) return 'PT';
    if (host.includes('newgardenshopping.nl')) return 'NL';
    const cfgCountry = rrConfig().defaultCountry;
    if (cfgCountry && cfgCountry !== 'auto') return String(cfgCountry).toUpperCase();
    return 'ES';
  }

  function updateAddressTypeUI() {
    const form = $('#rr-address-edit-form');
    if (collectionAddressType === 'new') {
      if (form) form.style.display = 'block';
    } else {
      if (form) form.style.display = 'none';
    }
    $$('.rr-address-type-card').forEach(c => c.classList.remove('rr-address-type-card--selected'));
    const card = $(`.rr-address-type-card[data-address-type="${collectionAddressType}"]`);
    if (card) card.classList.add('rr-address-type-card--selected');
  }

  function updateStep3UI() {
    const addressSection = $('#rr-collection-address-section');
    const packagingSection = $('#rr-collection-packaging-section');
    const selfShipSection = $('#rr-self-ship-section');
    const seurSection = $('#rr-seur-point-section');
    const isPickup = collectionMethod === 'pickup';
    const isSelf = collectionMethod === 'self';
    const isSeurPoint = collectionMethod === 'seur_point' && isSeurPointFeatureEnabled();
    if (addressSection) addressSection.style.display = isPickup ? 'block' : 'none';
    if (packagingSection) packagingSection.style.display = (isPickup || isSeurPoint) ? 'block' : 'none';
    // Self-ship nunca se muestra (opción no ofrecida)
    if (selfShipSection) selfShipSection.style.display = 'none';
    if (seurSection) seurSection.style.display = isSeurPoint ? 'block' : 'none';
    if (isPickup) initCollectionAddress();
    if (isSeurPoint) initSeurPointSearch();
    updatePackagingLimitsHint();
  }

  function resetSeurPointUI() {
    const results = $('#rr-seur-results');
    if (results) results.innerHTML = '';
    const wrap = $('#rr-seur-results-wrap');
    if (wrap) wrap.hidden = true;
    const count = $('#rr-seur-results-count');
    if (count) count.textContent = '';
    setSeurStatus('');
    const btn = $('#rr-seur-search-btn');
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
    renderSelectedSeurPickup();
  }

  function initSeurPointSearch() {
    const addr = getCollectionAddress() || orderData?.shippingAddress || {};
    const zipEl = $('#rr-seur-zip');
    const cityEl = $('#rr-seur-city');
    if (zipEl && !zipEl.value && addr.zip) zipEl.value = addr.zip;
    if (cityEl && !cityEl.value && addr.city) cityEl.value = addr.city;
    renderSelectedSeurPickup();
  }

  function setSeurStatus(msg, isError) {
    const status = $('#rr-seur-search-status');
    if (!status) return;
    status.classList.remove('rr-seur__status--error');
    if (!msg) {
      status.hidden = true;
      status.textContent = '';
      return;
    }
    status.hidden = false;
    status.textContent = msg;
    if (isError) status.classList.add('rr-seur__status--error');
  }

  function shortenSeurSchedule(schedule) {
    if (!schedule) return '';
    const parts = String(schedule).split(' · ').filter(Boolean);
    if (parts.length <= 2) return parts.join(' · ');
    return parts.slice(0, 2).join(' · ') + '…';
  }

  async function searchSeurPickups() {
    if (!isSeurPointFeatureEnabled() || !PROXY_URL) {
      setSeurStatus(S().seurUnavailable || 'Búsqueda de puntos SEUR no disponible.', true);
      return;
    }
    const zip = ($('#rr-seur-zip')?.value || '').trim();
    const city = ($('#rr-seur-city')?.value || '').trim();
    if (!zip || zip.length < 4) {
      setSeurStatus(S().seurZipInvalid || 'Introduce un código postal válido.', true);
      return;
    }
    if (!city) {
      setSeurStatus(S().seurCityRequired || 'Introduce la ciudad.', true);
      return;
    }
    const btn = $('#rr-seur-search-btn');
    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
    }
    setSeurStatus(S().seurSearching || 'Buscando…');
    const wrap = $('#rr-seur-results-wrap');
    const results = $('#rr-seur-results');
    if (wrap) wrap.hidden = true;
    if (results) results.innerHTML = '';

    try {
      const shopDomain = getShopDomain();
      const countryCode = getDefaultPickupCountryCode() || 'ES';
      const params = new URLSearchParams({
        shop: shopDomain,
        postalCode: zip,
        countryCode
      });
      params.set('cityName', city);
      const url = PROXY_URL.replace(/\/$/, '') + '/api/seur-pickups?' + params.toString();
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Shop-Domain': shopDomain
        }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setSeurStatus(data.error || S().seurLoadError || 'No se pudieron cargar los puntos SEUR.', true);
        return;
      }
      const pickups = Array.isArray(data.pickups) ? data.pickups : [];
      if (pickups.length === 0) {
        setSeurStatus(S().seurNone || 'No hay puntos cerca. Prueba otro código postal.', true);
        return;
      }
      setSeurStatus('');
      renderSeurResults(pickups);
    } catch (err) {
      console.error('[ReturnRequest] SEUR search error:', err);
      setSeurStatus(S().seurConnError || 'Error de conexión al buscar puntos SEUR.', true);
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    }
  }

  function renderSeurResults(pickups) {
    const results = $('#rr-seur-results');
    const wrap = $('#rr-seur-results-wrap');
    const count = $('#rr-seur-results-count');
    if (!results) return;
    if (wrap) wrap.hidden = false;
    if (count) {
      count.textContent = pickups.length === 1
        ? (S().seurOneFound || '1 punto encontrado')
        : `${pickups.length}${S().seurManyFound || ' puntos encontrados'}`;
    }

    results.innerHTML = pickups.map((p, idx) => {
      const title = escHtml(p.name || p.code || 'Punto SEUR');
      const line1 = escHtml([p.address].filter(Boolean).join(''));
      const line2 = escHtml(
        [p.code ? `Cód. ${p.code}` : '', [p.postalCode, p.city].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(' · ')
      );
      const schedule = shortenSeurSchedule(p.schedule);
      return (
        `<button type="button" class="rr-seur__item" role="option" data-seur-idx="${idx}" aria-selected="false" aria-label="Seleccionar ${title}">` +
        `<span class="rr-seur__item-indicator" aria-hidden="true">` +
        `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11.5 3.5L5.5 10L2.5 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>` +
        `</span>` +
        `<span class="rr-seur__item-body">` +
        `<span class="rr-seur__item-name">${title}</span>` +
        (line1 ? `<span class="rr-seur__item-meta">${line1}</span>` : '') +
        (line2 ? `<span class="rr-seur__item-meta">${line2}</span>` : '') +
        (schedule ? `<span class="rr-seur__item-hours">${escHtml(schedule)}</span>` : '') +
        `</span>` +
        `</button>`
      );
    }).join('');

    results.querySelectorAll('.rr-seur__item').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.getAttribute('data-seur-idx'), 10);
        const picked = pickups[idx];
        if (!picked) return;
        results.querySelectorAll('.rr-seur__item').forEach((el) => {
          el.setAttribute('aria-selected', el === btn ? 'true' : 'false');
        });
        selectedSeurPickup = {
          code: picked.code || '',
          name: picked.name || '',
          address: picked.address || '',
          postalCode: picked.postalCode || '',
          city: picked.city || '',
          phone: picked.phone || '',
          schedule: picked.schedule || '',
          latitude: picked.latitude || null,
          longitude: picked.longitude || null
        };
        renderSelectedSeurPickup();
        if (wrap) wrap.hidden = true;
        setSeurStatus('');
        validatePackagingStep();
      });
    });
  }

  function renderSelectedSeurPickup() {
    const box = $('#rr-seur-selected');
    const display = $('#rr-seur-selected-display');
    const search = document.querySelector('#rr-seur-point-section .rr-seur__search');
    const help = document.querySelector('#rr-seur-point-section .rr-seur__help');
    if (!box || !display) return;
    if (!selectedSeurPickup) {
      box.hidden = true;
      display.innerHTML = '';
      if (search) search.hidden = false;
      if (help) help.hidden = false;
      return;
    }
    const p = selectedSeurPickup;
    const lines = [
      p.name,
      p.address,
      [p.postalCode, p.city].filter(Boolean).join(' '),
      p.code ? `Código ${p.code}` : '',
      shortenSeurSchedule(p.schedule)
    ].filter(Boolean);
    display.innerHTML = lines.map((l) => `<span>${escHtml(l)}</span>`).join('');
    box.hidden = false;
    if (search) search.hidden = true;
    if (help) help.hidden = true;
  }

  function initCollectionAddress() {
    const checked = $('.rr-address-type-radio:checked');
    if (checked) collectionAddressType = checked.value;
    if (collectionAddressType === 'same' && orderData?.shippingAddress) {
      collectionAddress = Object.assign({}, orderData.shippingAddress);
    }
    updateAddressTypeUI();
  }

  /** Devuelve la dirección de recogida a usar: misma = envío del pedido; nueva = leída del formulario */
  function getCollectionAddress() {
    if (collectionMethod !== 'pickup') return null;
    if (collectionAddressType === 'same') return orderData?.shippingAddress || null;
    const get = (id) => { const el = $(id); return el ? el.value.trim() : ''; };
    const address1 = get('#rr-addr-address1');
    const province = get('#rr-addr-province');
    const countryCode = get('#rr-addr-country').toUpperCase();
    if (!address1 || !province || !countryCode) return null;
    return {
      name: orderData?.shippingAddress?.name || '',
      address1,
      address2: get('#rr-addr-address2'),
      zip: get('#rr-addr-zip'),
      city: get('#rr-addr-city'),
      province,
      countryCode,
      phone: get('#rr-addr-phone')
    };
  }

  function copyNewgardenAddress() {
    const text = 'NEWGARDEN SPAIN SL\nAv. Francia, 15\n30840 Alhama de Murcia, España\nTel: 968 97 88 06';
    const btn = $('#rr-copy-newgarden-address');
    const flash = (b) => {
      if (!b) return;
      const orig = b.innerHTML;
      b.textContent = S().copied || '¡Copiado!';
      setTimeout(() => { b.innerHTML = orig; }, 2000);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => flash(btn)).catch(() => fallbackCopy(text, btn));
    } else {
      fallbackCopy(text, btn);
    }
  }

  function fallbackCopy(text, btn) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0;';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); if (btn) { const o = btn.innerHTML; btn.textContent = S().copied || '¡Copiado!'; setTimeout(() => { btn.innerHTML = o; }, 2000); } } catch (e) { /* ignore */ }
    document.body.removeChild(ta);
  }

  function renderEstimatedRefund() {
    const section = $('#rr-estimated-refund-section');
    if (!section) return;
    const { grossSubtotal, discount, paidSubtotal } = getRefundBreakdown();
    const returnCost = getReturnCost(paidSubtotal);
    const totalEl = $('#rr-refund-total');
    const discountRow = $('#rr-refund-discount-row');
    const discountEl = $('#rr-refund-discount');
    const costEl = $('#rr-refund-cost');
    const netRow = $('#rr-refund-net-row');
    const netEl = $('#rr-refund-net');
    const consultEl = $('#rr-refund-consult');
    const recibirasEl = $('#rr-refund-recibiras');
    if (totalEl) totalEl.textContent = formatMoney(grossSubtotal);
    if (discount > 0 && discountRow && discountEl) {
      discountRow.style.display = 'flex';
      discountEl.textContent = '-' + formatMoney(discount);
    } else if (discountRow) {
      discountRow.style.display = 'none';
    }
    if (returnCost === null) {
      if (costEl) costEl.textContent = S().consultShort || 'Consultar *';
      if (netRow) netRow.style.display = 'none';
      if (consultEl) consultEl.style.display = 'block';
      if (recibirasEl) recibirasEl.style.display = 'none';
    } else {
      const netEuros = Math.max(0, paidSubtotal - returnCost);
      if (costEl) costEl.textContent = formatMoney(returnCost);
      if (netRow) netRow.style.display = 'flex';
      if (netEl) netEl.textContent = formatMoney(netEuros);
      if (consultEl) consultEl.style.display = 'none';
      if (recibirasEl) {
        const pre = S().recibirasPrefix || 'Recibirás';
        const suf = S().recibirasSuffix || ' en tu cuenta';
        recibirasEl.textContent = pre + ' ' + formatMoney(netEuros) + suf;
        recibirasEl.style.display = 'block';
      }
    }
  }

  /* ---------- Open / Close ---------- */
  function open() {
    const ol = overlay();
    if (!ol) return;
    resetForm();
    ol.classList.add('rr-active');
    ol.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rr-modal-open');
    ensureQtyInputsVisible();
  }

  /** Flujo invitado: omite paso 0 (tipo ya elegido en página). */
  function openAsDesistimiento() {
    open();
    returnType = 'desistimiento';
    showStep(1);
    currentStep = 1;
  }

  function close() {
    const ol = overlay();
    if (!ol) return;
    ol.classList.remove('rr-active');
    ol.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rr-modal-open');
    setTimeout(() => {
      if (!ol.classList.contains('rr-active')) resetForm();
    }, 500);
  }

  function ensureQtyInputsVisible() {
    $$('.rr-qty-input').forEach(input => {
      const max = Math.max(1, parseInt(input.dataset.max || input.getAttribute('max'), 10) || 1);
      let val = parseInt(input.value, 10);
      if (isNaN(val) || val < 1) val = max;
      if (val > max) val = max;
      input.value = String(val);
      const stepper = input.closest('.rr-stepper');
      if (stepper) {
        const display = $('.rr-stepper__display', stepper);
        if (display) display.textContent = String(val);
        const minus = $('.rr-stepper__btn--minus', stepper);
        const plus = $('.rr-stepper__btn--plus', stepper);
        if (minus) minus.disabled = val <= 1;
        if (plus) plus.disabled = val >= max;
      }
    });
  }

  function resetForm() {
    currentStep = 0;
    returnType = null;
    collectionMethod = null;
    collectionAddressType = 'same';
    collectionAddress = null;
    selectedSeurPickup = null;
    $$('.rr-type-radio').forEach(r => { r.checked = false; });
    $$('.rr-type-card').forEach(c => c.classList.remove('rr-type-card--selected'));
    toggleNoSeComoFuncionaBlock(false);
    // Reset collection method UI (solo recogida en domicilio)
    applyPickupOnlyDefault();
    // Reset dirección: misma por defecto, ocultar form, vaciar campos
    const sameRadio = $('.rr-address-type-radio[value="same"]');
    if (sameRadio) sameRadio.checked = true;
    $$('.rr-address-type-card').forEach(c => c.classList.remove('rr-address-type-card--selected'));
    const sameCard = $('.rr-address-type-card[data-address-type="same"]');
    if (sameCard) sameCard.classList.add('rr-address-type-card--selected');
    const addrForm = $('#rr-address-edit-form');
    if (addrForm) addrForm.style.display = 'none';
    ['#rr-addr-address1', '#rr-addr-address2', '#rr-addr-zip', '#rr-addr-city', '#rr-addr-province', '#rr-addr-country', '#rr-addr-phone'].forEach(sel => {
      const el = $(sel);
      if (el) el.value = '';
    });
    $$('.rr-product-card').forEach(card => {
      card.classList.remove('rr-selected');
      const cb = $('.rr-checkbox', card);
      if (cb) cb.checked = false;
      const qi = $('.rr-qty-input', card);
      if (qi) {
        const max = parseInt(qi.dataset.max || qi.getAttribute('max'), 10) || 1;
        qi.value = String(max);
        const stepper = qi.closest('.rr-stepper');
        if (stepper) {
          const display = $('.rr-stepper__display', stepper);
          if (display) display.textContent = String(max);
          const minus = $('.rr-stepper__btn--minus', stepper);
          const plus = $('.rr-stepper__btn--plus', stepper);
          if (minus) minus.disabled = max <= 1;
          if (plus) plus.disabled = true;
        }
      }
    });
    ensureQtyInputsVisible();
    $$('.rr-reason-radio').forEach(r => r.checked = false);
    const ta = $('#rr-observations');
    if (ta) ta.value = '';
    const cc = $('#rr-char-current');
    if (cc) cc.textContent = '0';
    const terms = $('#rr-terms-checkbox');
    if (terms) terms.checked = false;
    // Reset packaging: original, vaciar bultos
    const origRadio = $('#rr-packaging-original');
    if (origRadio) origRadio.checked = true;
    const bultosList = $('#rr-packaging-bultos-list');
    if (bultosList) bultosList.innerHTML = '';
    nextPackageNo = PACKAGE_NO_BASE;
    const originalMsg = $('#rr-packaging-original-message');
    if (originalMsg) originalMsg.style.display = 'flex';
    const bultosSection = $('#rr-packaging-bultos-section');
    if (bultosSection) bultosSection.classList.add('rr-packaging-bultos--hidden');
    validateTypeStep();
    showStep(0);
    const btn1 = $('#rr-next-step1');
    if (btn1) btn1.disabled = true;
    const btn2 = $('#rr-next-step2');
    if (btn2) btn2.disabled = true;
    const btnNext3 = $('#rr-next-step3');
    if (btnNext3) btnNext3.disabled = true;
    const btn3 = $('#rr-submit-btn');
    if (btn3) btn3.disabled = true;
  }

  /* ---------- Step Navigation ---------- */
  function goToStep(step) {
    if (step === 2 && !isStep1Valid()) return;
    if (step === 3 && !isStep2Valid()) return;
    if (step === 4 && !isPackagingStepValid()) return;
    if (step === 4) {
      try { populateReview(); } catch (e) { console.error('[ReturnRequest] populateReview failed:', e); }
    }
    showStep(step);
    currentStep = step;
    if (step === 2) {
      const checked = $('.rr-reason-radio:checked');
      toggleNoSeComoFuncionaBlock(checked && checked.value === REASON_NO_SE_COMO_FUNCIONA);
    }
    if (step === 3) syncStep3CollectionMethod();
  }

  function showStep(step) {
    $$('.rr-step').forEach(s => {
      s.classList.remove('rr-step--active', 'rr-step--slide-left');
      s.style.display = 'none';
    });
    const stepId = String(step);
    const target = $(`.rr-step[data-step="${stepId}"]`);
    if (target) {
      target.style.display = 'flex';
      target.classList.add('rr-step--active');
      const body = $('.rr-body', target);
      if (body) body.scrollTop = 0;
    }
  }

  /* ---------- Validation ---------- */
  function isStep1Valid() { return $$('.rr-checkbox:checked').length > 0; }
  function isStep2Valid() { return !!$('.rr-reason-radio:checked'); }

  function validateStep1() {
    const btn = $('#rr-next-step1');
    if (btn) btn.disabled = !isStep1Valid();
  }

  function validateStep2() {
    const btn = $('#rr-next-step2');
    if (btn) btn.disabled = !isStep2Valid();
  }

  /** Validación paso 3: método de recogida requerido; si pickup, dirección (misma o nueva válida) y embalaje */
  function isPackagingStepValid() {
    if (!collectionMethod) return false;
    if (collectionMethod === 'self') return true;
    if (collectionMethod === 'seur_point') {
      if (!isSeurPointFeatureEnabled() || !selectedSeurPickup) return false;
      // seur_point: embalaje original OK; personalizado = al menos un bulto válido
      const originalRadioSeur = $('#rr-packaging-original');
      if (originalRadioSeur?.checked) return true;
      const cardsSeur = $$('.rr-bulto-card');
      return cardsSeur.some(card => isBultoCardValid(card));
    }
    // pickup: dirección válida (misma = OK; nueva = obligatorio address1)
    if (collectionAddressType === 'new' && !getCollectionAddress()) return false;
    // pickup: embalaje original OK; personalizado = al menos un bulto válido
    const originalRadio = $('#rr-packaging-original');
    if (originalRadio?.checked) return true;
    const cards = $$('.rr-bulto-card');
    return cards.some(card => isBultoCardValid(card));
  }

  function isBultoCardValid(card) {
    const { length, width, height, weight } = getBultoMetrics(card);
    if ([length, width, height, weight].some((n) => isNaN(n))) return false;
    return validateBultoCardLimits(card).valid;
  }

  function isCustomBultosPackagingVisible() {
    if (collectionMethod === 'self') return false;
    const bultosSection = $('#rr-packaging-bultos-section');
    return !!(bultosSection && !bultosSection.classList.contains('rr-packaging-bultos--hidden'));
  }

  function validatePackagingStep() {
    const btn = $('#rr-next-step3');
    if (btn) btn.disabled = !isPackagingStepValid();
    updatePackagingLimitsHint();
    if (!isCustomBultosPackagingVisible()) return;
    const reqMsg = S().validationRequired || 'Obligatorio';
    const posMsg = S().validationPositive || 'Debe ser mayor que 0';
    $$('.rr-bulto-card').forEach(card => {
      const limits = validateBultoCardLimits(card);
      updateBultoCardVisuals(card);
      ['length', 'width', 'height', 'weight'].forEach(name => {
        const input = $('.rr-bulto-input[data-field="' + name + '"]', card);
        const err = $('.rr-bulto-error[data-field="' + name + '"]', card);
        if (!input) return;
        const raw = String(input.value ?? '').trim();
        const n = readBultoInputNumber(input);
        const basicOk = raw !== '' && Number.isFinite(n) && n > 0;
        let errText = '';
        if (!raw) errText = reqMsg;
        else if (!basicOk) errText = posMsg;
        else if (limits.fields[name]) errText = limits.fields[name];
        else if (name === 'weight' && limits.fields.weight) errText = limits.fields.weight;
        else if (name !== 'weight' && limits.fields._girth) errText = limits.fields._girth;
        else if (name !== 'weight' && limits.fields._dims) errText = limits.fields._dims;
        const dimWarn = !errText && (limits.fields._girth || limits.fields._dims) && name !== 'weight';
        if (input.classList) {
          input.classList.toggle('rr-input-error', !!errText);
          input.classList.toggle('rr-input-warn', dimWarn);
        }
        if (err) {
          err.textContent = errText;
          err.hidden = !errText;
        }
      });
    });
  }

  function validateStep3() {
    const terms = $('#rr-terms-checkbox');
    const btn = $('#rr-submit-btn');
    if (btn && terms) btn.disabled = !terms.checked;
  }

  /* ---------- Packaging (bultos) ---------- */
  function addBulto() {
    const list = $('#rr-packaging-bultos-list');
    if (!list) return;
    const ui = bultoFormStrings();
    const packageNo = nextPackageNo;
    nextPackageNo += PACKAGE_NO_BASE;
    const index = list.children.length + 1;
    const card = document.createElement('div');
    card.className = 'rr-bulto-card';
    card.dataset.packageNo = String(packageNo);
    card.innerHTML = `
      <div class="rr-bulto-card__header">
        <span class="rr-bulto-card__title">${escHtml(ui.cardTitle(index))}</span>
        <button type="button" class="rr-bulto-remove" aria-label="${escHtml(ui.removeAria)}" data-action="remove-bulto">×</button>
      </div>
      <div class="rr-bulto-card__grid">
        <div class="rr-bulto-field rr-bulto-field--dim">
          <label for="rr-bulto-length-${packageNo}">${escHtml(ui.fieldLength)}</label>
          <div class="rr-bulto-input-wrap">
            <input type="number" min="0.1" step="0.1" id="rr-bulto-length-${packageNo}" class="rr-bulto-input" data-field="length" placeholder="0" title="${escHtml(ui.inputTitle)}">
            <span class="rr-bulto-unit">cm</span>
          </div>
          <span class="rr-bulto-error" data-field="length" aria-live="polite"></span>
        </div>
        <div class="rr-bulto-field rr-bulto-field--dim">
          <label for="rr-bulto-width-${packageNo}">${escHtml(ui.fieldWidth)}</label>
          <div class="rr-bulto-input-wrap">
            <input type="number" min="0.1" step="0.1" id="rr-bulto-width-${packageNo}" class="rr-bulto-input" data-field="width" placeholder="0" title="${escHtml(ui.inputTitle)}">
            <span class="rr-bulto-unit">cm</span>
          </div>
          <span class="rr-bulto-error" data-field="width" aria-live="polite"></span>
        </div>
        <div class="rr-bulto-field rr-bulto-field--dim">
          <label for="rr-bulto-height-${packageNo}">${escHtml(ui.fieldHeight)}</label>
          <div class="rr-bulto-input-wrap">
            <input type="number" min="0.1" step="0.1" id="rr-bulto-height-${packageNo}" class="rr-bulto-input" data-field="height" placeholder="0" title="${escHtml(ui.inputTitle)}">
            <span class="rr-bulto-unit">cm</span>
          </div>
          <span class="rr-bulto-error" data-field="height" aria-live="polite"></span>
        </div>
        <div class="rr-bulto-field">
          <label for="rr-bulto-weight-${packageNo}">${escHtml(ui.fieldWeight)}</label>
          <div class="rr-bulto-input-wrap">
            <input type="number" min="0.01" step="0.01" id="rr-bulto-weight-${packageNo}" class="rr-bulto-input" data-field="weight" placeholder="0" title="${escHtml(ui.inputTitle)}">
            <span class="rr-bulto-unit">kg</span>
          </div>
          <span class="rr-bulto-error" data-field="weight" aria-live="polite"></span>
        </div>
      </div>
    `;
    list.appendChild(card);
    ensureBultoCardChrome(card);
    updateBultoCardVisuals(card);
    const removeBtn = $('[data-action="remove-bulto"]', card);
    if (removeBtn) removeBtn.addEventListener('click', function () { removeBulto(this); validatePackagingStep(); });
  }

  function removeBulto(button) {
    const card = button.closest('.rr-bulto-card');
    if (!card) return;
    const list = $('#rr-packaging-bultos-list');
    if (list && list.children.length <= 1) return;
    card.remove();
    validatePackagingStep();
  }

  /** Lee packaging del DOM para payload y review */
  function getPackagingData() {
    const originalRadio = $('#rr-packaging-original');
    const isOriginalPackaging = !!originalRadio?.checked;
    const packages = [];
    if (!isOriginalPackaging) {
      $$('.rr-bulto-card').forEach(card => {
        const packageNo = parseInt(card.dataset.packageNo, 10);
        if (isNaN(packageNo)) return;
        const get = (name) => {
          const input = $(`.rr-bulto-input[data-field="${name}"]`, card);
          if (!input) return null;
          const n = parseFloat(input.value, 10);
          return typeof n === 'number' && !isNaN(n) && n > 0 ? n : null;
        };
        const length = get('length');
        const width = get('width');
        const height = get('height');
        const weight = get('weight');
        if (length != null && width != null && height != null && weight != null) {
          packages.push({ packageNo, length, width, height, weight });
        }
      });
    }
    return { isOriginalPackaging, packages };
  }

  function buildPackagingPayload() {
    const { isOriginalPackaging, packages } = getPackagingData();
    return {
      packaging: {
        isOriginalPackaging,
        packages: isOriginalPackaging ? [] : packages
      }
    };
  }

  /* ---------- Quantity Controls ---------- */
  function adjustQty(button, delta) {
    const stepper = button.closest('.rr-stepper');
    if (!stepper) return;
    const input = $('.rr-qty-input', stepper);
    if (!input) return;
    const max = parseInt(input.dataset.max || input.getAttribute('max')) || 1;
    let val = parseInt(input.value) || 1;
    val = Math.min(Math.max(val + delta, 1), max);
    input.value = String(val);
    const display = $('.rr-stepper__display', stepper);
    if (display) display.textContent = String(val);
    const minus = $('.rr-stepper__btn--minus', stepper);
    const plus = $('.rr-stepper__btn--plus', stepper);
    if (minus) minus.disabled = val <= 1;
    if (plus) plus.disabled = val >= max;
  }

  function toggleSelectAll() {
    const allCards = $$('.rr-product-card');
    const allChecked = allCards.length > 0 && allCards.every(card => {
      const cb = $('.rr-checkbox', card);
      return cb && cb.checked;
    });
    allCards.forEach(card => {
      const cb = $('.rr-checkbox', card);
      if (!cb) return;
      cb.checked = !allChecked;
      card.classList.toggle('rr-selected', !allChecked);
      if (!allChecked) {
        const qtyInput = $('.rr-qty-input', card);
        if (qtyInput) {
          const max = parseInt(qtyInput.dataset.max || qtyInput.getAttribute('max'), 10) || 1;
          qtyInput.value = String(max);
          const stepper = qtyInput.closest('.rr-stepper');
          if (stepper) {
            const display = $('.rr-stepper__display', stepper);
            if (display) display.textContent = String(max);
            const minus = $('.rr-stepper__btn--minus', stepper);
            const plus = $('.rr-stepper__btn--plus', stepper);
            if (minus) minus.disabled = max <= 1;
            if (plus) plus.disabled = true;
          }
        }
      }
    });
    updateSelectAllButton();
    validateStep1();
    updateSeurPointOptionVisibility();
  }

  function updateSelectAllButton() {
    const btn = $('#rr-select-all-btn');
    if (!btn) return;
    const allCards = $$('.rr-product-card');
    const allChecked = allCards.length > 0 && allCards.every(card => {
      const cb = $('.rr-checkbox', card);
      return cb && cb.checked;
    });
    btn.textContent = allChecked ? (S().deselectAll || 'Deseleccionar todo') : (S().selectAll || 'Seleccionar todo');
  }

  /* ---------- Review Population ---------- */
  function populateReview() {
    const container = $('#rr-review-products');
    if (!container || !orderData) return;

    container.innerHTML = '';

    $$('.rr-product-card').forEach(card => {
      const cb = $('.rr-checkbox', card);
      if (!cb || !cb.checked) return;

      const qty = Math.max(1, parseInt($('.rr-qty-input', card)?.value, 10) || 1);
      const lineItemId = card.dataset.lineItemId;
      const item = orderData.lineItems?.find(i => String(i.id) === String(lineItemId));
      if (!item) return;

      const variantLabel = item.variantTitle && item.variantTitle !== 'Default Title'
        ? ` — ${item.variantTitle}` : '';

      const div = document.createElement('div');
      div.className = 'rr-review-item';
      div.innerHTML = `
        <div class="rr-review-item-img">
          ${item.image ? `<img src="${item.image}" alt="${item.productTitle}" width="44" height="44">` : ''}
        </div>
        <div class="rr-review-item-info">
          <div class="rr-review-item-name">${item.productTitle}${variantLabel}</div>
          <div class="rr-review-item-meta">${S().reviewQtyLabel || 'Cant. a devolver:'} ${qty}${item.priceFormatted ? ' · ' + item.priceFormatted : ''}</div>
        </div>
      `;
      container.appendChild(div);
    });

    const reasonRadio = $('.rr-reason-radio:checked');
    const reasonEl = $('#rr-review-reason');
    if (reasonRadio && reasonEl) {
      reasonEl.textContent = getReasonLabels()[reasonRadio.value] || reasonRadio.value;
    }

    const obs = $('#rr-observations');
    const obsSection = $('#rr-review-obs-section');
    const obsText = $('#rr-review-observations');
    if (obs && obsSection && obsText) {
      if (obs.value.trim()) {
        obsSection.style.display = 'block';
        obsText.textContent = obs.value.trim();
      } else {
        obsSection.style.display = 'none';
      }
    }

    const packagingSection = $('#rr-review-packaging-section');
    const packagingText = $('#rr-review-packaging');
    if (packagingSection && packagingText) {
      if (collectionMethod === 'self') {
        packagingSection.style.display = 'none';
      } else {
        const { isOriginalPackaging, packages } = getPackagingData();
        if (isOriginalPackaging) {
          packagingText.textContent = S().packagingOriginal || 'Embalaje original. Se tendrán en cuenta las dimensiones del producto.';
        } else if (packages.length > 0) {
          const parts = packages.map(p => `${p.length}×${p.width}×${p.height} cm, ${p.weight} kg`);
          const col = S().packageColis || ' bulto(s): ';
          packagingText.textContent = `${packages.length}${col}${parts.join(' · ')}`;
        } else {
          packagingText.textContent = '—';
        }
        packagingSection.style.display = 'block';
      }
    }

    // Logistics section
    const logisticsSection = $('#rr-review-logistics-section');
    const logisticsText = $('#rr-review-logistics-text');
    if (logisticsSection && logisticsText) {
      if (collectionMethod === 'pickup') {
        const addr = getCollectionAddress();
        if (addr && addr.address1) {
          const parts = [addr.name, addr.address1, addr.address2, [addr.zip, addr.city].filter(Boolean).join(' ')].filter(Boolean);
          logisticsText.textContent = `${S().logisticsPickupAt || 'Recogida solicitada en: '}${parts.join(', ')}`;
        } else {
          logisticsText.textContent = S().logisticsPickupHome || 'Recogida en domicilio';
        }
        logisticsSection.style.display = 'block';
      } else if (collectionMethod === 'seur_point' && selectedSeurPickup) {
        const p = selectedSeurPickup;
        const parts = [p.name, p.code, p.address, [p.postalCode, p.city].filter(Boolean).join(' ')].filter(Boolean);
        logisticsText.textContent = `${S().logisticsSeurPoint || 'Entrega en punto SEUR: '}${parts.join(', ')}`;
        logisticsSection.style.display = 'block';
      } else if (collectionMethod === 'self') {
        logisticsText.textContent = S().logisticsSelfShip || 'Envío manual a instalaciones de Newgarden (Av. Francia, 15 — 30840 Alhama de Murcia)';
        logisticsSection.style.display = 'block';
      } else {
        logisticsSection.style.display = 'none';
      }
    }

    // Estimated refund
    renderEstimatedRefund();
  }

  /* ---------- Build Request Payload ---------- */
  /** fullReasonDescription = motivo + observaciones (para BC). isFullReturn = true si se devuelve todo el pedido. */
  function buildPayload() {
    const selectedItems = [];
    const reasonRadio = $('.rr-reason-radio:checked');
    const reason = reasonRadio ? reasonRadio.value : 'OTHER';
    const selectedReasonText = getReasonLabels()[reason] || reason;
    const observations = $('#rr-observations')?.value?.trim() || '';
    const fullReasonDescription = observations ? selectedReasonText + ' — ' + observations : selectedReasonText;
    const requiresPickup = collectionMethod === 'pickup';
    const isSeurPoint = collectionMethod === 'seur_point' && !!selectedSeurPickup && isSeurPointFeatureEnabled();

    let totalOrderQty = 0;
    let totalReturnedQty = 0;

    $$('.rr-product-card').forEach(card => {
      const maxQty = parseInt(card.dataset.maxQty, 10) || 1;
      totalOrderQty += maxQty;

      const cb = $('.rr-checkbox', card);
      if (!cb || !cb.checked) return;

      const lineItemId = card.dataset.lineItemId;
      if (!lineItemId) return;

      const inputVal = parseInt($('.rr-qty-input', card)?.value, 10);
      const quantity = Math.min(maxQty, Math.max(1, isNaN(inputVal) ? 1 : inputVal));
      totalReturnedQty += quantity;

      const lineItemData = orderData?.lineItems?.find(i => String(i.id) === String(lineItemId));
      selectedItems.push({
        lineItemId: lineItemId,
        fulfillmentLineItemId: lineItemData?.fulfillmentLineItemId || null,
        quantity: quantity,
        returnReason: reason,
        customerNote: fullReasonDescription
      });
    });

    const isFullReturn = totalOrderQty > 0 && totalReturnedQty >= totalOrderQty;

    const { packaging } = buildPackagingPayload();

    const { discount: totalDiscountAmount, paidSubtotal: totalEuros } = getRefundBreakdown();
    const returnCost = getReturnCost(totalEuros);
    const refundEstimate = returnCost !== null ? Math.max(0, totalEuros - returnCost) : null;

    const payload = {
      orderId: orderData?.orderGid || `gid://shopify/Order/${orderData?.orderId}`,
      orderName: orderData?.orderName || null,
      fullReasonDescription,
      selectedReasonText,
      isFullReturn,
      requiresPickup,
      collectionMethod: collectionMethod || 'pickup',
      sameShippingAddress: collectionAddressType === 'same',
      collectionAddress: requiresPickup ? getCollectionAddress() : null,
      pickupCountryCode: requiresPickup
        ? ((getCollectionAddress()?.countryCode || orderData?.shippingAddress?.countryCode || orderData?.shippingAddress?.countryCodeV2 || getDefaultPickupCountryCode()) + '').toUpperCase()
        : null,
      pickupProvince: requiresPickup
        ? (getCollectionAddress()?.province || orderData?.shippingAddress?.province || '')
        : null,
      returnCost: returnCost !== null ? returnCost : 0,
      refundEstimate: refundEstimate !== null ? refundEstimate : 0,
      /** Descuento aplicado a las líneas seleccionadas → BC Total Discount Amount (OData: totalDiscountAmount) */
      totalDiscountAmount: Number((Math.max(0, Number(totalDiscountAmount) || 0)).toFixed(2)),
      returnLineItems: selectedItems.filter(it => it && it.quantity >= 1),
      deliveryCarrier: getOrderDeliveryCarrier() || null,
      packaging
    };

    if (isSeurPoint) {
      payload.seurPickup = {
        code: selectedSeurPickup.code || '',
        name: selectedSeurPickup.name || '',
        address: selectedSeurPickup.address || '',
        postalCode: selectedSeurPickup.postalCode || '',
        city: selectedSeurPickup.city || '',
        phone: selectedSeurPickup.phone || '',
        schedule: selectedSeurPickup.schedule || ''
      };
      // BC: Ref Pickup (50017) + dirección del punto; sin recogida a domicilio
      payload.refPickup = String(selectedSeurPickup.code || '').slice(0, 50);
      payload.requiresPickup = false;
      payload.sameShippingAddress = false;
      payload.collectionAddress = {
        name: selectedSeurPickup.name || '',
        address1: selectedSeurPickup.address || '',
        address2: selectedSeurPickup.name || '',
        zip: selectedSeurPickup.postalCode || '',
        city: selectedSeurPickup.city || '',
        phone: selectedSeurPickup.phone || '',
        countryCode: getDefaultPickupCountryCode() || 'ES'
      };
      payload.pickupCountryCode = getDefaultPickupCountryCode() || 'ES';
      payload.pickupProvince = '';
    }

    // Modo debugging: si en observaciones se escribe exactamente "ES26427", se envía ese valor como Nº Doc. Externo a BC
    if (observations === 'ES26427') {
      payload.externalDocumentNo = 'ES26427';
    }

    return payload;
  }

  /* ---------- Submission ---------- */
  async function submit() {
    const btn = $('#rr-submit-btn');
    const btnText = $('.rr-btn-text', btn);
    const btnLoader = $('.rr-btn-loader', btn);
    if (!btn || btn.disabled) return;

    btn.disabled = true;
    if (btnText) btnText.style.display = 'none';
    if (btnLoader) btnLoader.style.display = 'inline-flex';

    try {
      const payload = buildPayload();
      let result;

      if (PROXY_URL) {
        result = await submitViaProxy(payload);
      } else {
        result = await submitDirectly();
      }

      if (result.success) {
        showSuccess(result.returnId, result.returnName);
      } else {
        showError(result.errors);
      }
    } catch (err) {
      console.error('[ReturnRequest] Submission error:', err);
      showError([err.message || 'Error de conexión. Inténtalo de nuevo.']);
    } finally {
      if (btnText) btnText.style.display = 'inline';
      if (btnLoader) btnLoader.style.display = 'none';
      btn.disabled = false;
    }
  }

  /**
   * Submit via Vercel backend proxy → Shopify Admin API.
   * Sends shop domain as header for multi-store resolution.
   */
  async function submitViaProxy(payload) {
    const shopDomain = getShopDomain();
    const endpoint = PROXY_URL.replace(/\/$/, '') + '/api/returns';

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shop-Domain': shopDomain
      },
      body: JSON.stringify({
        shop: shopDomain,
        ...payload
      })
    });

    // Rate limit
    if (response.status === 429) {
      return { success: false, errors: ['Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.'] };
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        success: false,
        errors: data.errors || data.userErrors?.map(e => e.message) || [`Error del servidor (${response.status})`]
      };
    }

    const data = await response.json();

    if (data.errors?.length) {
      return { success: false, errors: data.errors };
    }

    if (data.userErrors?.length) {
      return { success: false, errors: data.userErrors.map(e => e.message) };
    }

    return {
      success: true,
      returnId: data.returnId || data.returnName || data.id || '',
      returnName: data.returnName || ''
    };
  }

  /**
   * Sin proxy no se puede crear la devolución en Shopify.
   * La Admin API (returnRequest) requiere token y solo puede llamarse desde servidor.
   */
  async function submitDirectly() {
    return {
      success: false,
      errors: [
        'El servicio de devoluciones no está configurado. ' +
        'Por favor, contacta con soporte o configura la URL del proxy en el tema (Devoluciones → Return Proxy URL).'
      ]
    };
  }

  /* ---------- Return Status (localStorage) ---------- */
  function getReturnStorageKey() {
    const orderId = orderData?.orderId || orderData?.orderGid || '';
    return orderId ? `rr_return_${orderId}` : null;
  }

  function checkReturnStatus() {
    const key = getReturnStorageKey();
    if (!key) return;
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        toggleReturnPill(true);
        fetchAndApplyReturnStatus();
      }
    } catch (e) { /* localStorage not available */ }
  }

  function fetchAndApplyReturnStatus() {
    if (!PROXY_URL || !orderData?.orderId) return;
    const shopDomain = window.Shopify?.shop || window.location.hostname;
    const url = PROXY_URL.replace(/\/$/, '') + '/api/returns?orderId=' + encodeURIComponent(orderData.orderId) + '&shop=' + encodeURIComponent(shopDomain);
    fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { 'X-Shop-Domain': shopDomain }
    })
      .then(function (r) {
        if (!r.ok) return null;
        return r.json();
      })
      .then(function (data) {
        if (data && typeof data.step === 'number') {
          applyReturnDrawerStep(data.step, data.returnName);
          toggleReturnPill(true, data.hasReturnableItems);
        }
      })
      .catch(function () {});
  }

  function applyReturnDrawerStep(step, returnName) {
    const drawer = $('#rr-return-drawer');
    if (!drawer) return;
    const progressFill = drawer.querySelector('.rr-return-drawer__progress-fill');
    const steps = drawer.querySelectorAll('.rr-return-drawer__step');
    const triggerBadge = drawer.querySelector('.rr-return-drawer__trigger-badge');
    const refEl = $('#rr-return-drawer-ref-id');
    const refBlock = $('#rr-return-drawer-ref');
    const progressWidth = step >= 4 ? 100 : step >= 3 ? 75 : step >= 2 ? 50 : 25;
    if (progressFill) progressFill.style.width = progressWidth + '%';
    steps.forEach(function (el, i) {
      const stepIndex = i + 1;
      el.classList.toggle('active', stepIndex <= step);
      // Only show "in-progress" dots on the current step, never on the last step (4)
      el.classList.toggle('in-progress', stepIndex === step && step < 4);
    });
    if (triggerBadge) {
      triggerBadge.textContent = step >= 4 ? (S().badgeRefunded || 'Reembolsado') : step >= 3 ? (S().badgeApproved || 'Aprobada') : (S().badgeInProgress || 'En proceso');
      triggerBadge.classList.remove('rr-return-drawer__trigger-badge--approved', 'rr-return-drawer__trigger-badge--refunded');
      if (step >= 4) triggerBadge.classList.add('rr-return-drawer__trigger-badge--refunded');
      else if (step >= 3) triggerBadge.classList.add('rr-return-drawer__trigger-badge--approved');
    }
    if (refEl && (returnName || '').trim()) {
      refEl.textContent = returnName.trim();
      if (refBlock) refBlock.style.display = 'block';
    }
  }

  function markReturnSubmitted(returnName) {
    const key = getReturnStorageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify({
        returnName: returnName || '',
        timestamp: Date.now()
      }));
    } catch (e) { /* ignore */ }
  }

  function toggleReturnPill(showPill, hasReturnableItems) {
    const btn = $('#btn-return-request');
    const pill = $('#rr-return-status-pill');
    const drawer = $('#rr-return-drawer');
    const drawerBody = $('#rr-return-drawer-body');
    const drawerTrigger = $('#rr-return-drawer-trigger');
    if (btn) {
      const hideButton = showPill && hasReturnableItems === false;
      btn.style.display = hideButton ? 'none' : '';
      const label = btn.querySelector('.rr-btn-return-label');
      if (label) label.textContent = showPill ? (S().btnReturnMore || 'Devolver más productos') : (S().btnRequestReturn || 'Solicitar Devolución');
    }
    if (pill) pill.style.display = showPill ? 'inline-flex' : 'none';
    if (drawer) {
      drawer.style.display = showPill ? 'block' : 'none';
      drawer.setAttribute('aria-hidden', showPill ? 'false' : 'true');
    }
    if (showPill) {
      const key = getReturnStorageKey();
      if (key) {
        try {
          const stored = JSON.parse(localStorage.getItem(key) || '{}');
          const refEl = $('#rr-return-drawer-ref-id');
          const refBlock = $('#rr-return-drawer-ref');
          if (refEl && (stored.returnName || '').trim()) {
            refEl.textContent = stored.returnName.trim();
            if (refBlock) refBlock.style.display = 'block';
          }
        } catch (e) { /* ignore */ }
      }
      if (drawerBody) {
        drawerBody.hidden = false;
        if (drawerTrigger) drawerTrigger.setAttribute('aria-expanded', 'true');
        drawerTrigger?.classList.add('rr-return-drawer__trigger--open');
      }
    } else {
      if (drawerBody) drawerBody.hidden = true;
      if (drawerTrigger) {
        drawerTrigger.setAttribute('aria-expanded', 'false');
        drawerTrigger.classList.remove('rr-return-drawer__trigger--open');
      }
    }
  }

  /* ---------- Result States ---------- */
  function showSuccess(returnId, returnName) {
    const refEl = $('#rr-success-ref-id');
    if (refEl) {
      refEl.textContent = returnName && returnName.trim()
        ? returnName.trim()
        : (returnId.includes('/') ? returnId.split('/').pop() : returnId);
    }
    markReturnSubmitted(returnName);
    toggleReturnPill(true);
    showStep('success');
    fetchAndApplyReturnStatus();
  }

  function showError(errors) {
    const msgEl = $('#rr-error-message');
    if (msgEl && errors?.length) {
      const joined = errors.join('. ');
      if (/invalid quantity/i.test(joined)) {
        msgEl.textContent = S().duplicateReturnError || 'Ya existe una solicitud de devolución para estos productos. No puedes solicitar otra devolución para los mismos artículos.';
        markReturnSubmitted('');
        toggleReturnPill(true);
      } else {
        msgEl.textContent = joined;
      }
    }
    showStep('error');
  }

  /* ---------- Public API ---------- */
  return { init, open, openAsDesistimiento, close, goToStep, goBackToTypeStep, adjustQty, submit, onTypeSelected, reloadOrderData, toggleSelectAll };
})();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ReturnRequest.init);
} else {
  ReturnRequest.init();
}
